/* Tenant billing + platform admin (payments, tenants, plans, bank accounts). */
import { activeUntilAfter, paymentRef, subAmount, trialEnd, trialLeft, validIban } from "../../domain/compliance";
import { evPaymentRejected, evPaymentSubmitted, evPaymentVerified } from "./events";
import { isISODate } from "../../domain/dates";
import { round2 } from "../../domain/payroll";
import type * as T from "../types";
import { E, fmtMoney, has, isInt, isNum, str, type Ctx } from "./ctx";
import type { PaymentRec } from "./state";

export function pubPayment(p: PaymentRec): T.Payment {
  const { seq: _s, aiPack: _a, ...pub } = p;
  void _s; void _a;
  if (!pub.tenantId) delete pub.tenantId;
  return pub;
}
const sortPayments = (ps: PaymentRec[]) => ps.slice().sort((a, b) => (a.date !== b.date ? (a.date < b.date ? 1 : -1) : b.seq - a.seq));

export function billingOps(c: Ctx) {
  const summaries = (): T.TenantSummary[] =>
    c.state.tenants
      .map((t) => { const st = c.stateFor(t.id); return st ? { ...t, aiCredits: st.aiTenant.used } : t; })
      .sort((a, b) => (a.since !== b.since ? (a.since < b.since ? 1 : -1) : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const pendingPayment = (id: string) => {
    const p = c.state.payments.find((x) => x.id === id);
    if (!p) throw E.notFound("payment not found");
    if (p.status !== "Pending") throw E.conflict("payment is not pending");
    return p;
  };
  const tenantSummary = (id: string) => c.state.tenants.find((t) => t.id === id);

  return {
    async getBilling(): Promise<T.BillingView> {
      c.principal("billing");
      const sub = c.state.subscription;
      return {
        subscription: sub, trialEnd: trialEnd(sub), trialLeft: trialLeft(sub, c.today),
        payments: sortPayments(c.state.payments.filter((p) => p.tenantId === c.tid)).map(pubPayment),
        bankAccounts: c.state.bankAccounts, planPrices: c.state.planPrices, tenant: c.state.settings.tenant,
      };
    },

    async submitPayment(input: T.PaymentInput): Promise<T.Payment> {
      const u = c.principal("billing");
      const prices = c.state.planPrices;
      const plan = str(input?.plan), cycle = str(input?.cycle), bank = str(input?.bank), sender = str(input?.sender), date = str(input?.date), receipt = str(input?.receiptKey);
      const aiPack = has(input, "aiPack") ? input.aiPack : 0;
      const fe: Record<string, string> = {};
      if (!(plan in prices)) fe.plan = "unknown plan";
      if (cycle !== "m" && cycle !== "y") fe.cycle = "must be m or y";
      if (!isInt(input?.seats) || input.seats < 1 || input.seats > 1000) fe.seats = "must be an integer between 1 and 1000";
      if (!isInt(aiPack) || aiPack < 0 || aiPack > 100) fe.aiPack = "must be an integer between 0 and 100";
      if (!isNum(input?.paid) || input.paid <= 0) fe.paid = "must be > 0";
      if (!isISODate(date)) fe.date = "valid date required";
      else if (date > c.today) fe.date = "cannot be in the future";
      if (!bank.trim()) fe.bank = "required";
      if (!sender.trim()) fe.sender = "required";
      if (receipt && !c.ownsKey(u, receipt)) fe.receiptKey = "invalid file key";
      if (Object.keys(fe).length) throw E.validation(fe);
      if (c.state.payments.some((p) => p.tenantId === c.tid && p.status === "Pending")) throw E.conflict("a payment is already pending verification");
      const amt = subAmount(prices, plan, cycle as T.Cycle, input.seats, aiPack);
      const t = c.state.settings.tenant;
      const n = c.nextId("payments", 1045, () => c.state.payments.reduce((m, p) => Math.max(m, p.seq), 0));
      const pay: PaymentRec = {
        id: "PAY-" + n, seq: n, aiPack, tenant: t.name, tenantId: c.tid, cr: t.cr, plan, cycle: cycle as T.Cycle, seats: input.seats,
        amount: amt.amount, vat: amt.vat, total: amt.total, ref: paymentRef(t.cr, n), date, bank: bank.trim(), sender: sender.trim(), paid: input.paid, status: "Pending",
      };
      if (receipt) pay.receiptKey = receipt;
      c.state.payments.push(pay);
      c.state.subscription.status = "pending";
      const ts = tenantSummary(c.tid);
      if (ts) ts.status = "Payment review";
      c.audit(u, "Submitted payment", "Payment", pay.id, `${plan} ${cycle} × ${input.seats} · SAR ${fmtMoney(amt.total)}`);
      evPaymentSubmitted(c, pay);
      return pubPayment(pay);
    },

    async adminOverview(): Promise<T.AdminOverview> {
      c.principal("admin");
      const ts = summaries();
      const o: T.AdminOverview = { tenants: ts.length, trials: 0, active: 0, pendingPayments: 0, mrr: 0, aiCreditsUsed: 0 };
      for (const t of ts) {
        if (t.status === "Trial") o.trials++;
        else if (t.status === "Active") { o.active++; o.mrr += (c.state.planPrices[t.plan] ?? 0) * t.seats; }
        o.aiCreditsUsed += t.aiCredits;
      }
      o.mrr = round2(o.mrr);
      o.pendingPayments = c.state.payments.filter((p) => p.status === "Pending").length;
      return o;
    },

    async adminPayments(status?: string): Promise<T.Payment[]> {
      c.principal("admin");
      if (status && !["Pending", "Verified", "Rejected"].includes(status)) throw E.field("status", "must be Pending, Verified or Rejected");
      return sortPayments(c.state.payments.filter((p) => !status || p.status === status)).map(pubPayment);
    },

    async verifyPayment(id: string): Promise<T.Payment> {
      const u = c.principal("admin");
      const pay = pendingPayment(id);
      pay.status = "Verified"; pay.by = u.name; pay.at = c.today;
      const tst = c.stateFor(pay.tenantId);
      if (tst) {
        const sub = tst.subscription;
        const te = trialEnd(sub);
        const start = te > c.today ? te : c.today;
        Object.assign(sub, { status: "active", plan: pay.plan, cycle: pay.cycle, seats: pay.seats, activeUntil: activeUntilAfter(start, pay.cycle), aiPack: pay.aiPack });
        const credits = c.state.aiPlatform.credits[pay.plan];
        if (credits != null) tst.aiTenant.limit = credits;
      }
      const ts = pay.tenantId ? tenantSummary(pay.tenantId) : undefined;
      if (ts) { ts.status = "Active"; ts.plan = pay.plan; ts.seats = pay.seats; }
      c.audit(u, "Verified payment", "Payment", pay.id, pay.tenant);
      evPaymentVerified(c, pay);
      return pubPayment(pay);
    },

    async rejectPayment(id: string, reason: string): Promise<T.Payment> {
      const u = c.principal("admin");
      const r = str(reason).trim();
      if (!r) throw E.field("reason", "required");
      const pay = pendingPayment(id);
      pay.status = "Rejected"; pay.reason = r; pay.by = u.name; pay.at = c.today;
      let summaryStatus = "Trial";
      const tst = c.stateFor(pay.tenantId);
      if (tst) {
        const sub = tst.subscription;
        if (trialEnd(sub) > c.today) sub.status = "trial";
        else { sub.status = "expired"; summaryStatus = "Trial ended"; }
      }
      const ts = pay.tenantId ? tenantSummary(pay.tenantId) : undefined;
      if (ts && ts.status !== "Active") ts.status = summaryStatus;
      c.audit(u, "Rejected payment", "Payment", pay.id, r);
      evPaymentRejected(c, pay);
      return pubPayment(pay);
    },

    async adminTenants(): Promise<T.TenantSummary[]> {
      c.principal("admin");
      return summaries();
    },

    async extendTrial(tenantId: string, days: number): Promise<T.TenantSummary> {
      const u = c.principal("admin");
      if (!isInt(days) || days < 1 || days > 90) throw E.field("days", "must be an integer between 1 and 90");
      const ts = tenantSummary(tenantId);
      if (!ts) throw E.notFound("tenant not found");
      const tst = c.stateFor(tenantId);
      if (tst) {
        const sub = tst.subscription;
        sub.trialDays += days;
        if (sub.status === "expired" && trialEnd(sub) > c.today) sub.status = "trial";
        if (sub.status === "trial") ts.status = "Trial";
      } else if (ts.status === "Trial ended") ts.status = "Trial";
      c.audit(u, "Extended trial", "Tenant", tenantId, `+${days} days`);
      return tst ? { ...ts, aiCredits: tst.aiTenant.used } : ts;
    },

    async adminPlans(): Promise<Record<string, number>> {
      c.principal("admin");
      return c.state.planPrices;
    },

    async adminBankAccounts(): Promise<T.BankAccount[]> {
      c.principal("admin");
      return c.state.bankAccounts;
    },

    async updatePlanPrices(prices: Record<string, number>): Promise<Record<string, number>> {
      const u = c.principal("admin");
      if (prices == null || typeof prices !== "object") throw E.badRequest("JSON object expected");
      const next = { ...c.state.planPrices };
      const fe: Record<string, string> = {};
      for (const [k, v] of Object.entries(prices)) {
        if (!["Starter", "Business", "Pro"].includes(k)) { fe[k] = "unknown plan"; continue; }
        if (!isNum(v) || v < 1 || v > 1000) { fe[k] = "must be between 1 and 1000"; continue; }
        next[k] = v;
      }
      if (Object.keys(fe).length) throw E.validation(fe);
      c.state.planPrices = next;
      c.audit(u, "Updated plan prices", "Plans", "", Object.keys(prices).sort().join(", "));
      return next;
    },

    async updateBankAccount(i: number, acct: Partial<T.BankAccount>): Promise<T.BankAccount[]> {
      const u = c.principal("admin");
      const accts = c.state.bankAccounts;
      if (!Number.isInteger(i) || i < 0 || i >= accts.length) throw E.notFound("bank account not found");
      const merged = { ...accts[i], ...(acct || {}) };
      if (!validIban(merged.iban)) throw E.field("iban", "IBAN must be SA followed by 22 digits");
      accts[i] = merged;
      c.audit(u, "Updated bank account", "BankAccount", String(i), merged.bank);
      return accts;
    },
  };
}
